<template>
  <div
    v-editable="blok"
    class="EventBlock"
    :class="desktopWidth('full')"
  >
    <div v-if="events.length > 0">
      <div
        class="type-headline-xl mobOnly:px-12 mb-16"
        :class="{
          'text-center desk:mb-32 desk:type-headline-2xl': blok.headlineSize === '2xl',
          'text-center desk:mb-56 desk:type-headline-3xl': blok.headlineSize === '3xl',
          'mobObly:mb-32': blok.headlineSize === '2xl' && blok.mobileDesign === 'grid-cols-1'
        }"
      >
        {{ blok.headline }}
      </div>
      <div
        class="empty:hidden type-headline-lg mobOnly:px-12 mb-16"
        :class="{
          'desk:type-headline-2xl desk:mb-24': blok.headlineSize === '3xl'
        }"
      >
        {{ blok.subHeadline }}
      </div>
      <div
        class="grid desk:gap-y-32 desk:gap-x-12 mobOnly:gap-y-16"
        :class="{
          'desk:w-half desk:mx-auto': events.length === 1,
          'desk:grid-cols-2': events.length === 2,
          'desk:grid-cols-3': events.length > 2,
          'mobOnly:grid-cols-2 mobOnly:gap-x-2 ': events.length > 1 && blok.mobileDesign === 'grid-cols-2'
        }"
      >
        <EventBanner
          v-for="event in events"
          :key="event._uid"
          :blok="event"
          :size-desktop="events.length > 2 ? 'half' : 'full'"
          :size="blok.mobileDesign === 'grid-cols-2' ? 'half':'full'"
        />
      </div>

      <div
        v-if="totalEvents > events.length && blok.showLoadMore"
        class="mt-32 desk:mt-64"
      >
        <LoadProgress
          :current="events.length"
          :total="totalEvents"
          text="events.loadMore"
          @load-more="loadMore"
        />
      </div>
    </div>

    <div v-else-if="loading" class="text-center">
      <LoadingIcon />
    </div>
  </div>
</template>

<script setup lang="ts">
import { EventEntry, StoryBlokPropBase } from '~/constants/types/storyblok';
import useDesktopWidth from '~/composeables/useDesktopWidth';

import { useUiStore } from '~/store/ui';
import LoadingIcon from '~/components/generic/LoadingIcon.vue';
import EventBanner from '~/components/events/EventBanner.vue';
import LoadProgress from '~/components/generic/LoadProgress.vue';
import useStoryblokAdmin from '~/composeables/useStoryblokAdmin';
const storyblokApi = useStoryblokApi();
const { isInStoryblokAdmin } = useStoryblokAdmin();

type Props = StoryBlokPropBase & {
  events: string; // number of events to load
  showLoadMore: boolean;
  headline: string;
  headlineSize?: '' | 'xl' | '2xl' | '3xl';
  mobileDesign: 'grid-cols-1' | 'grid-cols-2';
  eventType?: string,
  subHeadline: string,
}

const props = defineProps<{
  blok: Props,
}>();

const { desktopWidth } = useDesktopWidth(props);

const getYesterdayDate = (): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const year = yesterday.getFullYear();
  const month = (yesterday.getMonth() + 1).toString().padStart(2, '0');
  const day = yesterday.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day} 23:59:59`;
};

const loading = ref(true);
const page = ref(1);
const events = ref<EventEntry[]>([]);
const totalEvents = ref(-1);

const uiStore = useUiStore();

onMounted(()=> {
  load();
});

const load = async(append = false) => {
  loading.value = true;

  const filter_query = {
    startDate: {
      gt_date: getYesterdayDate(),
    },
  } as {
    startDate: { gt_date: string },
    sortCategory?: { in: string }
  };

  // sortCategory: { in: 'online' },
  if (props.blok.eventType && props.blok.eventType !== '') {
    filter_query.sortCategory = { in: props.blok.eventType };
  }

  const { data, headers } = await storyblokApi.get('cdn/stories/', {
    version: useRuntimeConfig().public.storyblokStoriesVersion as 'draft' | 'published',
    content_type: 'EventEntry',
    sort_by: 'content.startDate:asc',
    per_page: parseInt(props.blok.events),
    page: page.value,
    filter_query,
  });

  if (data && data.stories) {
    const newEvents = data.stories.map((m) => m.content);
    if (append) {
      events.value = events.value.concat(newEvents);
    } else {
      events.value = newEvents;
    }
    totalEvents.value = parseInt(headers.total);
  } else {
    uiStore.setTemporaryError('Couldn\'t load events from storyblok', 5000);
  }

  loading.value = false;

};

const loadMore = () => {
  page.value++;
  load(true);
};

// update in storyblok
if (isInStoryblokAdmin()) {
  watch(()=> props.blok.events, () => {
    page.value = 1;
    load();
  });
}

</script>

<style scoped lang="postcss">
</style>
